import { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { CreateUserRaceRequest } from './home-types';

/**
 * Create your own race — send us tracks, we build the replay.
 *
 * Files go through the EXISTING presigned-upload path (POST /uploads/presign →
 * S3 PUT → complete), not as email attachments: VKX files run 10–15 MB each and
 * a real fleet would never make it through a mail server. The submission record
 * (POST /me/races) is what notifies us and what appears under Your races.
 *
 * Uploads run ONE AT A TIME on purpose — /uploads/presign is rate-limited to
 * 20/min and the admin dropzone has already tripped it by firing in parallel.
 */

interface PresignResponse {
  uploadId: string;
  presignedUrl: string;
  s3Key: string;
}

type FileState = {
  file: File;
  progress: number;
  rawTrackId?: string;
  error?: string;
};

const ACCEPT = '.vkx,.gpx,.zip';
const MAX_BYTES = 100 * 1024 * 1024;

function fmtSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function putToS3(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

export function CreateRacePage() {
  const { isAuthenticated } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [whereWhen, setWhereWhen] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<FileState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // We need somewhere to put the result and someone to email.
  if (!isAuthenticated) return <Navigate to="/signup?redirect=%2Fcreate-race" replace />;

  async function addFiles(picked: FileList | null) {
    if (!picked?.length) return;
    setError(null);

    const accepted: FileState[] = [];
    for (const file of Array.from(picked)) {
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is over 100 MB — send that one to hello@regattareplay.com instead.`);
        continue;
      }
      accepted.push({ file, progress: 0 });
    }
    if (!accepted.length) return;

    const base = files.length;
    setFiles((prev) => [...prev, ...accepted]);
    setUploading(true);

    // Sequential, deliberately — see the note at the top of this file.
    for (let i = 0; i < accepted.length; i++) {
      const idx = base + i;
      const { file } = accepted[i];
      try {
        const presign = await api.post<PresignResponse>('/uploads/presign', {
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });
        await putToS3(presign.presignedUrl, file, (pct) =>
          setFiles((prev) => prev.map((f, n) => (n === idx ? { ...f, progress: pct } : f))),
        );
        await api.post(`/uploads/${presign.uploadId}/complete`, {
          fileName: file.name,
          s3Key: presign.s3Key,
          fileSize: file.size,
        });
        setFiles((prev) =>
          prev.map((f, n) => (n === idx ? { ...f, progress: 100, rawTrackId: presign.uploadId } : f)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setFiles((prev) => prev.map((f, n) => (n === idx ? { ...f, error: message } : f)));
      }
    }
    setUploading(false);
  }

  const uploaded = files.filter((f) => f.rawTrackId);
  const canSubmit = !!name.trim() && uploaded.length > 0 && !uploading && !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateUserRaceRequest = {
        name: name.trim(),
        ...(whereWhen.trim() && { whereWhen: whereWhen.trim() }),
        ...(note.trim() && { note: note.trim() }),
        rawTrackIds: uploaded.map((f) => f.rawTrackId!),
      };
      await api.post('/me/races', body);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? `${err.message} — your tracks are uploaded; email hello@regattareplay.com and we'll pick it up.`
          : 'Could not send your race',
      );
      setSubmitting(false);
    }
  }

  const field =
    'w-full rounded-lg border border-viewer-line2/40 bg-viewer-panel px-3 py-2.5 text-[13.5px] text-viewer-ink placeholder:text-viewer-faint focus:border-viewer-accent focus:outline-none focus:ring-2 focus:ring-viewer-accent/25';
  const label = 'block text-[11.5px] font-medium text-viewer-muted mb-1.5';

  if (sent) {
    return (
      <div className="min-h-[70vh] bg-viewer-deep px-4 py-12">
        <Helmet>
          <title>Race sent - RegattaReplay™</title>
        </Helmet>
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-viewer-good/30 bg-viewer-good/10 text-xl text-viewer-good">
            ✓
          </div>
          <h1 className="font-viewer-display text-[26px] font-bold text-viewer-ink">We’ve got it</h1>
          <p className="mt-1 text-[13px] text-viewer-muted">
            {name.trim()}
            <br />
            {uploaded.length} {uploaded.length === 1 ? 'track' : 'tracks'} ·{' '}
            {fmtSize(uploaded.reduce((n, f) => n + f.file.size, 0))}
          </p>

          <div className="mt-6 border-t border-viewer-line/25 pt-5 text-left">
            {[
              ['1', 'Your tracks are in.', 'They’re in our storage — nothing else to do.'],
              ['2', 'We build the race.', 'Marks, course, analytics — usually a day or two.'],
              ['3', 'We email you the link.', 'It also appears under Your races.'],
            ].map(([n, head, tail]) => (
              <div key={n} className="mb-2.5 flex items-start gap-2.5">
                <span className="mt-0.5 rounded border border-viewer-accent/30 px-1.5 py-0.5 font-viewer-mono text-[9px] text-viewer-accent">
                  {n}
                </span>
                <span className="text-[12.5px] text-viewer-muted">
                  <b className="font-semibold text-viewer-ink">{head}</b> {tail}
                </span>
              </div>
            ))}
          </div>

          <Link
            to="/"
            className="mt-5 block rounded-lg bg-brand-ink px-4 py-2.5 text-[13.5px] font-semibold text-white hover:bg-brand-dark"
          >
            Back to home
          </Link>
          <p className="mt-2 text-[11.5px] text-viewer-faint">Questions? hello@regattareplay.com</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-viewer-deep px-4 py-10">
      <Helmet>
        <title>Create your own race - RegattaReplay™</title>
      </Helmet>
      <div className="mx-auto max-w-md">
        <h1 className="font-viewer-display text-[26px] font-bold text-viewer-ink">Create your own race</h1>
        <p className="mt-1 text-[13px] text-viewer-muted">
          Send us your GPS tracks — we’ll build the replay and email you when it’s ready.
        </p>

        <div className="mt-6">
          <label htmlFor="race-name" className={label}>
            Race name
          </label>
          <input
            id="race-name"
            className={field}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Wednesday night series — Race 3"
            autoFocus
          />
        </div>

        <div className="mt-3.5">
          <label htmlFor="race-where" className={label}>
            Where and when
          </label>
          <input
            id="race-where"
            className={field}
            value={whereWhen}
            onChange={(e) => setWhereWhen(e.target.value)}
            placeholder="RCYC · 22 July 2026"
          />
        </div>

        <div className="mt-3.5">
          <p className={label}>GPS tracks</p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-xl border border-dashed border-viewer-line2/40 bg-viewer-panel/50 px-4 py-6 text-center hover:border-viewer-accent/40"
          >
            <span className="block text-xl text-viewer-accent">↑</span>
            <span className="mt-1.5 block text-[13px] font-semibold text-viewer-ink">
              Choose VKX or GPX files
            </span>
            <span className="mt-0.5 block text-[11px] text-viewer-faint">
              One per boat · up to 100 MB each
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {files.map((f, i) => (
            <div key={`${f.file.name}-${i}`} className="mt-2">
              <div className="flex items-center gap-2.5 rounded-lg border border-viewer-line/25 bg-viewer-panel px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[12px] text-viewer-ink">{f.file.name}</span>
                <span className="font-viewer-mono text-[10px] text-viewer-faint">{fmtSize(f.file.size)}</span>
                <span
                  className={`font-viewer-mono text-[10px] ${
                    f.error ? 'text-viewer-warn' : f.rawTrackId ? 'text-viewer-good' : 'text-viewer-accent'
                  }`}
                >
                  {f.error ? 'failed' : f.rawTrackId ? 'done' : `${f.progress}%`}
                </span>
              </div>
              {!f.rawTrackId && !f.error && (
                <div className="mt-1 h-[3px] overflow-hidden rounded-sm bg-viewer-line2/20">
                  <div className="h-full rounded-sm bg-viewer-accent" style={{ width: `${f.progress}%` }} />
                </div>
              )}
              {f.error && <p className="mt-1 text-[11px] text-viewer-warn">{f.error}</p>}
            </div>
          ))}
        </div>

        <div className="mt-3.5">
          <label htmlFor="race-note" className={label}>
            Anything we should know? <span className="font-normal text-viewer-faint">— optional</span>
          </label>
          <textarea
            id="race-note"
            rows={3}
            className={field}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Start was around 18:35, two general recalls…"
          />
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12.5px] text-viewer-warn">
            {error}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="mt-4 w-full rounded-lg bg-brand-ink px-4 py-3 text-[13.5px] font-semibold text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-viewer-line2/20 disabled:text-viewer-faint"
        >
          {submitting ? 'Sending…' : 'Send to RegattaReplay'}
        </button>
        {!canSubmit && (
          <p className="mt-2 text-center text-[11.5px] text-viewer-faint">
            {uploading
              ? 'Waiting for uploads to finish…'
              : 'Add a race name and at least one track'}
          </p>
        )}
      </div>
    </div>
  );
}
